<template>
  <div>
    <HeaderDefault />

    <!-- Hero Section -->
    <div class="relative">
      <!-- Cover Image -->
      <div
        v-if="events.data[0].attributes.cover.data"
        class="h-[300px] bg-cover bg-center"
        :style="`background-image: url('${events.data[0].attributes.cover.data.attributes.url}')`"
      >
        <div
          class="absolute inset-0 bg-gradient-to-b from-black/50 to-black"
        ></div>
      </div>
      <div
        v-else
        class="h-[300px] bg-cover bg-center bg-blue-100"
        style="background-image: var(--bg-events-svg);"
      >
        <div
          class="absolute inset-0 bg-gradient-to-b from-transparent to-black/50"
        ></div>
      </div>

      <!-- Event Title Overlay -->
      <div class="absolute bottom-0 left-0 right-0 p-8 text-white">
        <div class="container mx-auto px-8 max-w-screen-lg">
          <h1
            class="text-2xl sm:text-4xl font-bold mb-4 items-center inline-flex"
          >
            {{ events.data[0].attributes.title }}

            <client-only>
              <Vue3Lottie
                v-if="events.data[0].attributes.isPublished"
                class="w-5 h-5 flex-shrink-0"
                animationLink="/animation/10.json"
                :height="60"
                :width="60"
              />
            </client-only>
          </h1>
        </div>
      </div>

      <!-- Breadcrumb -->
      <EventsBreadcrumb
        :category="{
          id: events.data[0].attributes.aa_category?.data?.id,
          title: events.data[0].attributes.aa_category?.data?.attributes?.title,
        }"
        :tag="
          events.data[0].attributes.aa_tags?.data?.[0]
            ? {
                id: events.data[0].attributes.aa_tags.data[0].id,
                title:
                  events.data[0].attributes.aa_tags.data[0].attributes.title,
              }
            : undefined
        "
        :title="events.data[0].attributes.title"
      />
    </div>

    <!-- Navigation Bar -->
    <nav
      class="sticky top-0 z-50 bg-white/90 backdrop-blur-sm border-b border-gray-200 shadow-sm"
    >
      <div class="container mx-auto max-w-screen-lg px-4">
        <div class="flex items-center justify-between h-16">
          <!-- Left Nav Items -->
          <div class="flex space-x-6">
            <a
              v-for="item in subtabs"
              :key="item.slug"
              :href="item.slug"
              class="inline-flex items-center px-4 py-2 text-sm-plus font-medium text-gray-700 hover:text-primary-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-900/10 transition-all"
            >
              <Icon :name="item.icon" class="w-4 h-4 mr-2" />
              {{ item.name }}
            </a>
          </div>

          <!-- Download Button -->
          <div v-if="events.data[0].attributes.download_url.data">
            <a
              :href="events.data[0].attributes.download_url.data.attributes.url"
              target="_blank"
              class="inline-flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-all group"
            >
              <Icon
                name="heroicons:arrow-down-tray"
                class="w-5 h-5 mr-2 group-hover:-translate-y-0.5 transition-transform"
              />
              <span>Download Brochure</span>
              <Icon
                name="heroicons:arrow-top-right-on-square"
                class="w-4 h-4 ml-2 opacity-75"
              />
            </a>
          </div>
        </div>
      </div>
    </nav>

    <!-- Main Content -->
    <div class="container mx-auto max-w-screen-lg py-4">
      <div class="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <!-- Right Column - Detailed Content -->
        <div class="lg:col-span-7 space-y-8">
          <!-- Event Details -->
          <section id="about" class="bg-white rounded-xl shadow-sm p-8">
            <div class="prose max-w-none">
              <MDC
                :value="events.data[0].attributes.description"
                tag="article"
              />
            </div>
          </section>
        </div>

        <!-- Left Column - Key Information -->
        <div class="lg:col-span-5">
          <div class="lg:sticky lg:top-20 bg-gray-100 shadow-xl p-5 space-y-4">
            <!-- Key Information Cards -->
            <div class="bg-white rounded-lg p-6">
              <h3
                class="text-lg font-semibold text-yellow-900 mb-2 flex items-center"
              >
                <Icon name="heroicons:calendar" class="w-5 h-5 mr-2" />
                Starts
              </h3>
              <NuxtTime
                :datetime="events.data[0].attributes.starts"
                class="text-yellow-800"
                year="numeric"
                month="long"
                day="numeric"
              />
            </div>

            <div class="bg-white rounded-lg p-6">
              <h3
                class="text-lg font-semibold text-green-900 mb-2 flex items-center"
              >
                <Icon name="heroicons:flag" class="w-5 h-5 mr-2" />
                Ends
              </h3>
              <NuxtTime
                :datetime="events.data[0].attributes.ends"
                class="text-green-800"
                year="numeric"
                month="long"
                day="numeric"
              />
            </div>

            <div class="bg-white rounded-lg p-6">
              <h3
                class="text-lg font-semibold text-blue-900 mb-2 flex items-center"
              >
                <Icon name="heroicons:map-pin" class="w-5 h-5 mr-2" />
                Location
              </h3>
              <p class="text-blue-800">
                {{ events.data[0].attributes.address }}
              </p>
            </div>

            <!-- Call to Action Buttons -->
            <a
              :href="
                'https://www.google.com/maps?q=' +
                events.data[0].attributes.address
              "
              target="_blank"
              class="flex items-center justify-between bg-green-600 text-white rounded-xl p-6 hover:bg-green-700 transition-colors w-full"
            >
              <div>
                <h3 class="text-xl font-semibold mb-1">How to Reach?</h3>
                <p class="text-green-100">Get directions to the venue</p>
              </div>
              <Icon name="heroicons:map" class="w-6 h-6" />
            </a>

            <a
              :href="events.data[0].attributes.registration_url"
              target="_blank"
              id="#visit"
              class="flex items-center justify-between bg-blue-600 text-white rounded-xl p-6 hover:bg-blue-700 transition-colors w-full"
            >
              <div>
                <h3 class="text-xl font-semibold mb-1">How to Register?</h3>
                <p class="text-blue-100">Complete your registration</p>
              </div>
              <Icon name="heroicons:arrow-right" class="w-6 h-6" />
            </a>
          </div>
        </div>
      </div>
    </div>

    <!-- Similar Events -->
    <div class="container mx-auto max-w-screen-lg py-8 border-t">
      <h2 class="text-2xl font-bold mb-6">Similar Events</h2>

      <div
        v-if="store.recommendedEvents?.length"
        class="grid grid-cols-1 md:grid-cols-2 gap-6"
      >
        <EventsCard
          v-for="event in store.recommendedEvents"
          :key="event.id"
          :event="event"
        />
      </div>
      <div v-else class="text-gray-600 text-center py-8">
        No similar events found
      </div>
    </div>
    <UBanner></UBanner>
    <FooterDefault />
  </div>
</template>

<script setup lang="ts">
const route = useRoute();
const scActive = ref(0);
const faqActive = ref(-1);

const store = useEventStore();

const { find } = useStrapi()

const { data: events } = await useAsyncData("event", () =>
  find("aa-events", {
    populate: "download_url,cover,aa_category,aa_tags",
    sort: "createdAt:desc",
    filters: {
      slug: {
        $eq: route.params.id,
      },
    },
  })
);

// Load recommended events if not already loaded
onMounted(async () => {
  await store.getRecommendedEvents({
    categoryId: events.value.data[0].attributes.aa_category?.data?.id,
    tagId: events.value.data[0].attributes.aa_tags?.data?.[0]?.id,
    currentEventId: events.value.data[0].id,
  });
});

const subtabs = ref([
  { name: "About", slug: "#about", icon: "heroicons:information-circle" },
  //   { name: "Register", slug: "#visit", icon: "heroicons:user-plus" },
]);

// Add computed property for the current URL
const currentUrl = computed(() => {
  return (
    window?.location?.href ||
    `https://academicalerts.com/events/${route.params.id}`
  );
});

// Add computed properties for meta content
const metaDescription = computed(() => {
  const desc = events.value?.data[0]?.attributes?.description || "";
  return desc.substring(0, 160).replace(/<[^>]*>/g, "") + "...";
});

const metaImage = computed(() => {
  return (
    events.value?.data[0]?.attributes?.cover?.data?.attributes?.url ||
    "https://academicalerts.com/images/placeholder.jpg"
  );
});

// Update useHead with enhanced meta tags
useHead({
  title: events.value?.data[0]?.attributes?.title + " - Academic Alerts",
  meta: [
    { name: "description", content: metaDescription.value },
    { name: "author", content: "Academic Alerts" },

    // Open Graph
    { property: "og:title", content: events.value?.data[0]?.attributes?.title },
    { property: "og:description", content: metaDescription.value },
    { property: "og:image", content: metaImage.value },
    { property: "og:url", content: currentUrl.value },
    { property: "og:type", content: "event" },
    { property: "og:site_name", content: "Academic Alerts" },

    // Twitter
    { name: "twitter:card", content: "summary_large_image" },
    {
      name: "twitter:title",
      content: events.value?.data[0]?.attributes?.title,
    },
    { name: "twitter:description", content: metaDescription.value },
    { name: "twitter:image", content: metaImage.value },

    // Event specific
    {
      name: "event:start_date",
      content: events.value?.data[0]?.attributes?.starts,
    },
    {
      name: "event:end_date",
      content: events.value?.data[0]?.attributes?.ends,
    },
    {
      name: "event:location",
      content: events.value?.data[0]?.attributes?.address,
    },
  ],
  script: [
    {
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Event",
        name: events.value?.data[0]?.attributes?.title,
        description: events.value?.data[0]?.attributes?.description,
        startDate: events.value?.data[0]?.attributes?.starts,
        endDate: events.value?.data[0]?.attributes?.ends,
        location: {
          "@type": "Place",
          name: events.value?.data[0]?.attributes?.address,
          address: events.value?.data[0]?.attributes?.address,
        },
        image: events.value?.data[0]?.attributes?.cover?.data?.attributes?.url,
        organizer: {
          "@type": "Organization",
          name: "Academic Alerts",
          url: "https://academicalerts.com",
        },
        eventStatus: "https://schema.org/EventScheduled",
        eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
        offers: {
          "@type": "Offer",
          url: currentUrl.value,
          availability: "https://schema.org/InStock",
        },
      }),
    },
  ],
});
</script>

<style>
.prose {
  @apply text-gray-700;
}
.prose :where(h2):not(:where([class~="not-prose"] *)) {
  @apply text-2xl font-bold mt-8 mb-4;
}
.prose :where(p):not(:where([class~="not-prose"] *)) {
  @apply mb-4;
}
</style>
